import {
  ArrowRight, AtSign, Check, CheckCircle2, ChevronDown, CircleAlert, Clock3,
  Copy, Eye, EyeOff, Filter, Inbox, LayoutGrid, ListChecks, LockKeyhole,
  LogOut, Mail, RefreshCw, Search, ShieldCheck, X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { DashboardResponse, DeliveryStatus, InboxMessage } from "../shared/types";
import { InboxView, messageTitle, RecordReader } from "./Inbox";
import { BilingualTitle, TitleTranslationControl } from "./BilingualTitle";
import { clearTitleTranslation, startTitleTranslation } from "./titleTranslation";

type LoadState = "loading" | "ready" | "error";
type View = "inbox" | "overview" | "records";
type AuthStage = "checking" | "login" | "authenticated";

// Capture once (including React StrictMode remounts) and remove the one-use ticket
// from the document URL immediately. It is never stored in browser storage.
const quickToolsTicket = window.self !== window.top
  ? /^#quicktools-ticket=([A-Za-z0-9_-]{43})$/.exec(window.location.hash)?.[1] : undefined;
if (quickToolsTicket) window.history.replaceState(null, "", window.location.pathname + window.location.search);
const quickToolsSession = quickToolsTicket ? fetch("/api/auth/quicktools", {
  method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ ticket: quickToolsTicket }),
}).then(async response => {
  if (!response.ok) throw new Error("QuickTools 登录已失效，请点击上方的重新加载。");
  return response.json() as Promise<{ authenticated?: boolean }>;
}).catch(() => ({ authenticated: false })) : null;

const EMPTY_DASHBOARD: DashboardResponse = {
  generatedAt: new Date(0).toISOString(),
  forwarding: { domain: "zzjmail.com", destination: "zackzhou1@gmail.com" },
  stats: { today: 0, week: 0, aliases: 0, successRate: null },
  trend: [], aliases: [], records: [], total: 0,
};
const STATUS_LABEL: Record<DeliveryStatus, string> = {
  processing: "处理中", forwarded: "已转发", failed: "转发失败",
};
const STATUS_ICON = { processing: Clock3, forwarded: CheckCircle2, failed: CircleAlert };

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(new Date(value));
}

function StatusBadge({ status }: { status: DeliveryStatus }) {
  const Icon = STATUS_ICON[status];
  return <span className={"status status--" + status}><Icon aria-hidden="true" />{STATUS_LABEL[status]}</span>;
}

function TrendChart({ data, loading, unavailable, expanded, onToggle }: {
  data: DashboardResponse["trend"]; loading: boolean; unavailable: boolean; expanded: boolean; onToggle: () => void;
}) {
  const max = Math.max(1, ...data.map((point) => point.count));
  const hasData = data.some((point) => point.count > 0);
  return (
    <section className={"panel disclosure-panel" + (expanded ? " is-expanded" : "")} aria-labelledby="trend-heading">
      <div className="panel-heading">
        <div><h2 id="trend-heading">近 14 天邮件趋势</h2><p>按实际收件日期统计</p></div>
        <button className="icon-button disclosure-toggle" type="button" onClick={onToggle}
          aria-expanded={expanded} aria-controls="trend-content" aria-label={expanded ? "收起邮件趋势" : "展开邮件趋势"}>
          <ChevronDown aria-hidden="true" />
        </button>
      </div>
      <div id="trend-content" className="disclosure-content">
        {loading || unavailable ? <div className="chart-empty" aria-busy={loading}>{loading ? "正在加载趋势…" : "趋势暂不可用"}</div> : !hasData ? (
          <div className="chart-empty"><Clock3 aria-hidden="true" /><span>收到邮件后，趋势会显示在这里</span></div>
        ) : (
          <div className="chart" role="img" aria-label={"近 14 天邮件数量：" + data.map((point) => point.day + "，" + point.count + " 封").join("；")}>
            {data.map((point, index) => (
              <div className="chart-column" key={point.day} aria-hidden="true">
                <span className="chart-value">{point.count > 0 ? point.count : ""}</span>
                <div className="chart-track"><div className="chart-bar" style={{ height: (point.count / max) * 100 + "%" }} /></div>
                <span className="chart-label">{index % 2 === 0 || index === data.length - 1 ? point.day.slice(5) : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function AliasPanel({ data, domain, loading, unavailable, expanded, onToggle, onSelect }: {
  data: DashboardResponse["aliases"]; domain: string; loading: boolean; unavailable: boolean; expanded: boolean;
  onToggle: () => void; onSelect: (alias: string) => void;
}) {
  return (
    <section className={"panel alias-panel disclosure-panel" + (expanded ? " is-expanded" : "")} aria-labelledby="alias-heading">
      <div className="panel-heading">
        <div><h2 id="alias-heading">常用别名</h2><p>按累计收件量排序 · 点击查看记录</p></div>
        <button className="icon-button disclosure-toggle" type="button" onClick={onToggle}
          aria-expanded={expanded} aria-controls="alias-content" aria-label={expanded ? "收起常用别名" : "展开常用别名"}>
          <ChevronDown aria-hidden="true" />
        </button>
      </div>
      <div id="alias-content" className="disclosure-content">
        {loading || unavailable ? <div className="chart-empty" aria-busy={loading}>{loading ? "正在加载别名…" : "别名暂不可用"}</div> : data.length === 0 ? (
          <div className="chart-empty"><AtSign aria-hidden="true" /><span>尚无别名</span></div>
        ) : (
          <ol className="alias-list" aria-label="按收件量排序的别名">
            {data.map((item) => (
              <li key={item.alias}>
                <button type="button" onClick={() => onSelect(item.alias)} aria-label={"查看 " + item.alias + "@" + domain + " 的转发记录，" + item.count + " 封"}>
                  <span className="alias-address"><strong>{item.alias}</strong><span>@{domain}</span></span>
                  <span className="alias-count">{item.count} 封</span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function EmptyRecords({ filtered, domain, onReset }: { filtered: boolean; domain: string; onReset: () => void }) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  useEffect(() => {
    if (copyState !== "copied") return;
    const timeout = window.setTimeout(() => setCopyState("idle"), 3000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);
  async function copyAddress() {
    try { await navigator.clipboard.writeText("test@" + domain); setCopyState("copied"); }
    catch { setCopyState("error"); }
  }
  return (
    <div className="records-empty">
      {filtered ? <Search className="empty-symbol" aria-hidden="true" /> : <Inbox className="empty-symbol" aria-hidden="true" />}
      <h3>{filtered ? "没有符合条件的邮件" : "还没有邮件记录"}</h3>
      <p>{filtered ? "试试其他关键词，或清除筛选查看全部记录。" : <>发一封邮件到 <span className="test-address">test@{domain}</span>，收件后即可查看转发记录。</>}</p>
      {filtered ? <button className="button" type="button" onClick={onReset}>清除筛选</button> : (
        <button className="button" type="button" onClick={() => void copyAddress()}>
          {copyState === "copied" ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copyState === "copied" ? "已复制地址" : "复制测试地址"}
        </button>
      )}
      <span className={copyState === "error" ? "copy-error" : "sr-only"} role="status">
        {copyState === "error" ? "无法访问剪贴板，请选中上方地址手动复制。" : copyState === "copied" ? "测试地址已复制" : ""}
      </span>
    </div>
  );
}

function RecordTitle({ record, onOpen }: { record: InboxMessage; onOpen: (record: InboxMessage) => void }) {
  return <button className="record-title" aria-label={"查看全文：" + messageTitle(record)} onClick={() => onOpen(record)}>
    <strong><BilingualTitle title={messageTitle(record)} /></strong><span>{record.contentStatus === "stored" ? "查看全文" : "查看详情 · 原文待补入"}<ArrowRight aria-hidden="true" /></span>
  </button>;
}

function MobileRecord({ record, onOpen }: { record: InboxMessage; onOpen: (record: InboxMessage) => void }) {
  return (
    <article className="mobile-record">
      <div className="mobile-record__top"><strong>{record.recipient}</strong><StatusBadge status={record.status} /></div>
      <RecordTitle record={record} onOpen={onOpen} />
      <dl>
        <div><dt>发件人</dt><dd>{record.sender}</dd></div>
        <div><dt>转发到</dt><dd>{record.forwardedTo}</dd></div>
        <div><dt>收到时间</dt><dd><time dateTime={record.receivedAt}>{formatDateTime(record.receivedAt)}</time></dd></div>
      </dl>
    </article>
  );
}

function LoginPage({ stage, password, error, busy, onPasswordChange, onLogin }: {
  stage: Exclude<AuthStage, "authenticated">; password: string; error: string; busy: boolean;
  onPasswordChange: (value: string) => void; onLogin: (event: React.FormEvent) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-heading">
        <div className="login-brand"><Mail aria-hidden="true" /><span>ZZJ Mail</span><span className="login-domain">zzjmail.com</span></div>
        {stage === "checking" ? (
          <div className="login-loading" role="status"><RefreshCw className="spinning" aria-hidden="true" /><h1 id="login-heading">正在检查登录状态…</h1></div>
        ) : (
          <>
            <div className="login-title"><LockKeyhole aria-hidden="true" /><h1 id="login-heading">访问邮件看板</h1><p>输入密码，查看收件与转发记录。</p></div>
            <form onSubmit={onLogin} aria-busy={busy}>
              <label htmlFor="login-password">访问密码</label>
              <div className="password-field">
                <input id="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password}
                  onChange={(event) => onPasswordChange(event.target.value)} placeholder="输入密码" required
                  aria-invalid={Boolean(error)} aria-describedby={error ? "login-error login-hint" : "login-hint"} />
                <button className="icon-button" type="button" onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "隐藏密码" : "显示密码"} aria-pressed={showPassword}>
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </div>
              {error && <div id="login-error" className="login-error" role="alert">{error}</div>}
              <button className="button button--primary" type="submit" disabled={busy || password.length === 0}>
                {busy && <RefreshCw className="spinning" aria-hidden="true" />}{busy ? "正在登录…" : "进入看板"}
              </button>
            </form>
            <p className="login-hint" id="login-hint">使用与 zijianzhou.com 相同的密码</p>
          </>
        )}
        <div className="login-privacy"><ShieldCheck aria-hidden="true" /><span>私有存储 · 邮件仅登录后可见</span></div>
      </section>
    </main>
  );
}

export function App() {
  const [dashboard, setDashboard] = useState(EMPTY_DASHBOARD);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [hasLoaded, setHasLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [days, setDays] = useState("30");
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState<View>("inbox");
  const [refreshKey, setRefreshKey] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [trendExpanded, setTrendExpanded] = useState(false);
  const [aliasesExpanded, setAliasesExpanded] = useState(false);
  const [authStage, setAuthStage] = useState<AuthStage>("checking");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<InboxMessage | null>(null);
  const updateRecord = useCallback((message: InboxMessage) => {
    setSelectedRecord(previous => previous?.id === message.id ? message : previous);
    setDashboard(previous => ({ ...previous, records: previous.records.map(record => record.id === message.id ? message : record) }));
  }, []);

  useEffect(() => {
    if (authStage === "authenticated") void startTitleTranslation();
    else { setSelectedRecord(null); clearTitleTranslation(); }
  }, [authStage]);
  useEffect(() => {
    window.addEventListener("pagehide", clearTitleTranslation);
    return () => window.removeEventListener("pagehide", clearTitleTranslation);
  }, []);

  useEffect(() => {
    const expired = () => {
      setAuthStage("login"); setDashboard(EMPTY_DASHBOARD); setHasLoaded(false); setOffset(0);
      setAuthError("登录已过期，请重新输入密码。");
    };
    window.addEventListener("mail-auth-expired", expired);
    return () => window.removeEventListener("mail-auth-expired", expired);
  }, []);

  const loadDashboard = useCallback(async (signal: AbortSignal) => {
    setLoadState("loading");
    const params = new URLSearchParams({
      timezoneOffset: String(new Date().getTimezoneOffset()), query, status, days, limit: "50", offset: String(offset),
    });
    try {
      const response = await fetch("/api/dashboard?" + params, { signal, credentials: "same-origin" });
      if (signal.aborted) return;
      if (response.status === 401) {
        setAuthStage("login"); setDashboard(EMPTY_DASHBOARD); setHasLoaded(false); setOffset(0);
        setAuthError("登录已过期，请重新输入密码。"); setLoadState("ready"); return;
      }
      if (!response.ok) throw new Error("HTTP " + response.status);
      const data: DashboardResponse = await response.json();
      if (signal.aborted) return;
      setDashboard((previous) => offset === 0 ? data : {
        ...data, records: [...new Map([...previous.records, ...data.records].map((record) => [record.id, record])).values()],
      });
      setHasLoaded(true); setLoadState("ready");
    } catch {
      if (!signal.aborted) setLoadState("error");
    }
  }, [days, query, offset, status]);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        if (quickToolsSession && !(await quickToolsSession).authenticated) throw new Error("QuickTools session unavailable");
        const response = await fetch("/api/auth/status", { signal: controller.signal, credentials: "same-origin" });
        const payload: { authenticated?: boolean } = await response.json();
        if (controller.signal.aborted) return;
        setAuthStage(response.ok && payload.authenticated ? "authenticated" : "login");
      } catch {
        if (controller.signal.aborted) return;
        setAuthError("暂时无法检查登录状态，请检查网络后重试。"); setAuthStage("login");
      }
    })();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (authStage !== "authenticated" || view === "inbox") return;
    const controller = new AbortController();
    // Keep existing rows and metrics visible while a replacement request is pending.
    setLoadState("loading");
    const timeout = window.setTimeout(() => void loadDashboard(controller.signal), 220);
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [authStage, loadDashboard, refreshKey, view]);

  const isLoading = loadState === "loading";
  const initialLoading = !hasLoaded && isLoading;
  const filtered = Boolean(query.trim() || status !== "all" || days !== "30" || dashboard.stats.aliases > 0);
  const activeFilters = Number(status !== "all") + Number(days !== "30");
  const metrics = [
    { label: "今日邮件", value: String(dashboard.stats.today) },
    { label: "本周邮件", value: String(dashboard.stats.week) },
    { label: "已用别名", value: String(dashboard.stats.aliases) },
    { label: "转发成功率", value: dashboard.stats.successRate === null ? "—" : dashboard.stats.successRate + "%" },
  ];

  function navigate(next: View) {
    setView(next);
    window.requestAnimationFrame(() => {
    const target = document.getElementById(next === "records" ? "records-heading" : "top");
    target?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth", block: "start" });
    if (next === "records") target?.focus({ preventScroll: true });
    });
  }
  function refresh() { setOffset(0); setRefreshKey((value) => value + 1); }
  function resetFilters() { setQuery(""); setStatus("all"); setDays("all"); setOffset(0); }
  function selectAlias(alias: string) { setQuery(alias + "@" + dashboard.forwarding.domain); setStatus("all"); setDays("all"); setOffset(0); navigate("records"); }

  async function login(event: React.FormEvent) {
    event.preventDefault();
    if (authBusy) return;
    setAuthBusy(true); setAuthError("");
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "same-origin", body: JSON.stringify({ password: authPassword, embedded: window.self !== window.top }),
      });
      const payload: { authenticated?: boolean; error?: string } = await response.json();
      if (!response.ok || !payload.authenticated) { setAuthError(payload.error ?? "登录失败，请检查密码后重试。"); return; }
      setAuthPassword(""); setAuthStage("authenticated"); setLoadState("loading"); refresh();
    } catch { setAuthError("无法连接服务器，请检查网络后重试。"); }
    finally { setAuthBusy(false); }
  }

  async function logout() {
    if (logoutBusy) return;
    setLogoutBusy(true); setLogoutError("");
    try {
      const response = await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error("Logout failed");
      setDashboard(EMPTY_DASHBOARD); setHasLoaded(false); setOffset(0); setAuthPassword(""); setAuthError(""); setAuthStage("login");
    } catch { setLogoutError("退出失败，请检查网络后再次点击退出。"); }
    finally { setLogoutBusy(false); }
  }

  if (authStage !== "authenticated") return (
    <LoginPage stage={authStage} password={authPassword} error={authError} busy={authBusy}
      onPasswordChange={(value) => { setAuthPassword(value); setAuthError(""); }} onLogin={(event) => void login(event)} />
  );

  return (
    <div className={"app-shell" + (view === "inbox" ? " is-inbox" : "")}>
      <a className="skip-link" href={view === "inbox" ? "#mail-list-heading" : "#records-heading"}>{view === "inbox" ? "跳至邮件列表" : "跳至转发记录"}</a>
      {view !== "inbox" && <aside className="sidebar" aria-label="主导航">
        <a href="#top" className="brand" aria-label="ZZJ Mail 首页" onClick={(event) => { event.preventDefault(); navigate("overview"); }}><Mail aria-hidden="true" /><span>ZZJ Mail</span></a>
        <span className="brand-domain">zzjmail.com</span>
        <nav>
          <button onClick={() => navigate("inbox")}><Inbox aria-hidden="true" />收件箱</button>
          <button className={view === "overview" ? "active" : ""} aria-current={view === "overview" ? "location" : undefined} onClick={() => navigate("overview")}><LayoutGrid aria-hidden="true" />总览</button>
          <button className={view === "records" ? "active" : ""} aria-current={view === "records" ? "location" : undefined} onClick={() => navigate("records")}><ListChecks aria-hidden="true" />转发记录</button>
        </nav>
        <div className="sidebar-privacy"><ShieldCheck aria-hidden="true" /><span>邮件与附件私有存储</span></div>
      </aside>}

      <main id="top">
        <header className="topbar">
          {view === "inbox" ? <h1 className="topbar-title"><Mail aria-hidden="true" />ZZJ Mail</h1> : <span className="topbar-title">{view === "overview" ? "总览" : "转发记录"}</span>}
          {view !== "inbox" && <a href="#top" className="brand brand--mobile" onClick={(event) => { event.preventDefault(); navigate("overview"); }}><Mail aria-hidden="true" /><span>ZZJ Mail</span></a>}
          <div className="topbar-actions">
            <TitleTranslationControl />
            {view !== "inbox" && <span className="private-label"><LockKeyhole aria-hidden="true" />私有访问</span>}
            <button className="button button--plain logout-button" type="button" onClick={() => void logout()} disabled={logoutBusy} aria-label="退出登录"><LogOut aria-hidden="true" /><span>{logoutBusy ? "正在退出…" : "退出"}</span></button>
            <button className="button refresh-button" type="button" onClick={refresh} disabled={isLoading} aria-label={isLoading ? "正在刷新" : "刷新数据"}><RefreshCw className={isLoading ? "spinning" : ""} aria-hidden="true" /><span>刷新</span></button>
          </div>
        </header>

        {view === "inbox" && logoutError && <div className="error-banner" role="alert">{logoutError}</div>}
        {view === "inbox" ? <InboxView refreshKey={refreshKey} onOverview={() => navigate("overview")} /> : <div className="content">
          <section className="intro">
            <h1>邮件转发看板</h1><p>查看收件别名、转发去向与投递状态</p>
            <div className="flow" aria-label={"所有 " + dashboard.forwarding.domain + " 邮件转发到 " + dashboard.forwarding.destination}>
              <span className="flow-label"><Mail aria-hidden="true" />转发规则</span>
              <code>*@{dashboard.forwarding.domain}</code><ArrowRight className="flow-arrow" aria-hidden="true" /><code>{dashboard.forwarding.destination}</code>
            </div>
          </section>

          <section className="metrics" aria-label="邮件统计" aria-busy={initialLoading}>
            {metrics.map((item) => <div className="metric" key={item.label}><span>{item.label}</span><strong>{hasLoaded ? item.value : "—"}</strong></div>)}
          </section>

          <nav className="mobile-tabs" aria-label="看板导航">
            <button onClick={() => navigate("inbox")}>收件箱</button>
            <button aria-current={view === "overview" ? "location" : undefined} onClick={() => navigate("overview")}>总览</button>
            <button aria-current={view === "records" ? "location" : undefined} onClick={() => navigate("records")}>转发记录</button>
          </nav>

          {logoutError && <div className="error-banner" role="alert"><CircleAlert aria-hidden="true" /><span>{logoutError}</span><button className="icon-button" aria-label="关闭退出错误提示" onClick={() => setLogoutError("")}><X aria-hidden="true" /></button></div>}
          {loadState === "error" && <div className="error-banner" role="alert"><CircleAlert aria-hidden="true" /><span>{hasLoaded ? "更新失败，当前显示上次加载的数据。请检查网络后重试。" : "无法加载转发记录，请检查网络后重试。"}</span><button className="button button--plain" onClick={() => setRefreshKey((value) => value + 1)}>重试</button></div>}

          <div className="analytics-grid">
            <TrendChart data={dashboard.trend} loading={initialLoading} unavailable={!hasLoaded && loadState === "error"} expanded={trendExpanded} onToggle={() => setTrendExpanded((value) => !value)} />
            <AliasPanel data={dashboard.aliases} domain={dashboard.forwarding.domain} loading={initialLoading} unavailable={!hasLoaded && loadState === "error"} expanded={aliasesExpanded} onToggle={() => setAliasesExpanded((value) => !value)} onSelect={selectAlias} />
          </div>

          <section className="records-panel" id="records" aria-labelledby="records-heading">
            <div className="records-heading"><h2 id="records-heading" tabIndex={-1}>转发记录</h2><span className="records-count">{hasLoaded ? dashboard.total + " 条记录" : "等待加载"}</span></div>
            <div className="records-toolbar">
              <div className="search-field">
                <Search aria-hidden="true" /><label className="sr-only" htmlFor="record-search">搜索标题、发件人或收件别名</label>
                <input id="record-search" value={query} onChange={(event) => { setQuery(event.target.value); setOffset(0); }} placeholder="搜索标题、发件人或收件别名" />
                {query && <button className="icon-button search-clear" type="button" aria-label="清除搜索" onClick={() => { setQuery(""); setOffset(0); document.getElementById("record-search")?.focus(); }}><X aria-hidden="true" /></button>}
              </div>
              <button className={"button mobile-filter" + (activeFilters ? " has-filters" : "")} type="button" aria-expanded={filtersOpen} aria-controls="record-filters" onClick={() => setFiltersOpen((value) => !value)}><Filter aria-hidden="true" />筛选{activeFilters > 0 && <span className="filter-count">{activeFilters}</span>}</button>
              <div className={"filters" + (filtersOpen ? " filters--open" : "")} id="record-filters">
                <select aria-label="状态" value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}><option value="all">全部状态</option><option value="forwarded">已转发</option><option value="processing">处理中</option><option value="failed">转发失败</option></select>
                <select aria-label="时间范围" value={days} onChange={(event) => { setDays(event.target.value); setOffset(0); }}><option value="7">过去 7 天</option><option value="30">过去 30 天</option><option value="90">过去 90 天</option><option value="all">全部时间</option></select>
              </div>
            </div>
            <div className="record-results" aria-busy={isLoading}>
              <div className="table-wrap">
                <table><caption className="sr-only">邮件收件与转发状态</caption>
                  <thead><tr><th scope="col">收到时间</th><th scope="col">邮件标题 / 发件人</th><th scope="col">收件别名</th><th scope="col">转发到</th><th scope="col">状态</th></tr></thead>
                  <tbody>{dashboard.records.map((record) => (
                    <tr key={record.id}><td><time dateTime={record.receivedAt}>{formatDateTime(record.receivedAt)}</time></td><td><RecordTitle record={record} onOpen={setSelectedRecord} /><span className="record-sender">{record.sender}</span></td><td className="recipient-cell">{record.recipient}</td><td>{record.forwardedTo}</td><td><StatusBadge status={record.status} /></td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="mobile-records">{dashboard.records.map((record) => <MobileRecord key={record.id} record={record} onOpen={setSelectedRecord} />)}</div>
              {hasLoaded && dashboard.records.length === 0 && <EmptyRecords filtered={filtered} domain={dashboard.forwarding.domain} onReset={resetFilters} />}
              {initialLoading && <div className="records-loading"><RefreshCw className="spinning" aria-hidden="true" /><span>正在加载转发记录…</span></div>}
              {!hasLoaded && loadState === "error" && <div className="records-loading"><CircleAlert aria-hidden="true" /><span>记录暂时不可用，请点击上方「重试」。</span></div>}
            </div>
            <div className="result-count">
              <span role="status" aria-live="polite">{isLoading ? offset > 0 ? "正在加载更多记录…" : "正在更新…" : loadState === "error" ? "未能更新" : dashboard.total > 0 ? "已显示 " + dashboard.records.length + " 条，共 " + dashboard.total + " 条" : "暂无记录"}</span>
              {dashboard.total > dashboard.records.length ? <button className="button button--plain" type="button" disabled={isLoading} onClick={() => { setOffset(dashboard.records.length); setRefreshKey((value) => value + 1); }}>加载更多</button> : hasLoaded && <span className="updated-time">更新于 {formatDateTime(dashboard.generatedAt)}</span>}
            </div>
          </section>
          <footer><ShieldCheck aria-hidden="true" /><span>邮件原文与附件私有保存 · 登录后可查看</span></footer>
        </div>}
      </main>
      {selectedRecord && <RecordReader key={selectedRecord.id} message={selectedRecord} onClose={() => setSelectedRecord(null)} onMessageChange={updateRecord} />}
    </div>
  );
}
